import {
  Button,
  Input,
  makeStyles,
  shorthands,
  tokens,
  TabList,
  Tab,
  SelectTabData,
  SelectTabEvent,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { ChevronDown20Regular, CalendarLtr20Regular } from '@fluentui/react-icons';
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import BookingCalendar from '../components/BookingCalendar';
import OrderDetailsDialog from '../components/OrderDetailsDialog';
import { apiFetch } from '../lib/apiFetch';

// Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_API_KEY || '';

const useStyles = makeStyles({
  container: {
    ...shorthands.padding('1rem'),
    height: 'calc(100vh - 2rem)',
    display: 'flex',
    flexDirection: 'column',
  },
  contentCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    ...shorthands.overflow('hidden'),
  },
  mapContainer: {
    width: '100%',
    height: '500px',
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    marginBottom: '16px',
  },
  coordinatesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxWidth: '400px',
  },
  label: {
    fontSize: '14px',
    fontWeight: 600,
    marginBottom: '4px',
  },
  calendarContainer: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.overflow('auto'),
  },
  bookingContent: {
    ...shorthands.padding('24px'),
  },
  tabContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  calendarTabMenu: {
    backgroundColor: 'transparent',
    ...shorthands.border('none'),
    ...shorthands.padding('12px', '16px'),
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
    ...shorthands.borderBottom('3px', 'solid', 'transparent'),
    transition: 'all 0.2s ease',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  calendarTabMenuActive: {
    color: tokens.colorBrandForeground1,
    ...shorthands.borderBottom('3px', 'solid', tokens.colorBrandStroke1),
  },
  calendarIcon: {
    color: tokens.colorBrandForeground1,
  },
});

type TabValue = 'calendar' | 'booking';
type CalendarView = 'month' | 'week' | 'day';

interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  startDate: string;
  endDate: string;
  status: string;
}

export default function Booking() {
  const styles = useStyles();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [lng, setLng] = useState(-67.0308); // Default to Puerto Rico
  const [lat, setLat] = useState(18.2208);
  const [zoom, setZoom] = useState(9);
  const [mapStyle, setMapStyle] = useState<'streets' | 'satellite'>('streets');
  const [selectedTab, setSelectedTab] = useState<TabValue>('calendar');
  const [calendarView, setCalendarView] = useState<CalendarView>('week');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Fetch orders from Booqable
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(`/booqable/orders?per_page=500`);

        if (!response.ok) {
          throw new Error('Failed to fetch orders');
        }

        const data = await response.json();

        // Build customer lookup map
        const customerMap = new Map();
        if (data.included) {
          data.included.forEach((resource: any) => {
            if (resource.type === 'customers') {
              customerMap.set(resource.id, resource.attributes);
            }
          });
        }

        // Map orders
        const mappedOrders: Order[] = (data.data || []).map((order: any) => {
          const customerId = order.relationships?.customer?.data?.id;
          const customerData = customerId ? customerMap.get(customerId) : null;
          const customerName = customerData?.name || 'Unknown';

          return {
            id: order.id,
            orderNumber: order.attributes?.number || 'N/A',
            customerName: customerName,
            startDate: order.attributes?.starts_at || order.attributes?.start_at || '',
            endDate: order.attributes?.stops_at || order.attributes?.stop_at || '',
            status: order.attributes?.status,
          };
        });

        setOrders(mappedOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Convert orders to calendar events
  const calendarEvents = orders.map(order => ({
    id: order.id,
    title: `Order #${order.orderNumber} - ${order.customerName}`,
    startDate: new Date(order.startDate),
    endDate: new Date(order.endDate),
    color: getColorForStatus(order.status),
    priority: order.status,
  }));

  // Get color based on order status
  function getColorForStatus(status: string): string {
    switch (status) {
      case 'draft': return '#D6D9DB';
      case 'reserved': return '#62A1C6';
      case 'started': return '#ffb442';
      case 'stopped': return '#62da7c';
      case 'archived': return '#D6D9DB';
      case 'canceled': return '#D6D9DB';
      default: return '#E3D5F3';
    }
  }



  useEffect(() => {
    // Load Booqable script
    /*
    const script = document.createElement('script');
    script.src = 'https://5dfde28b-06c0-4d9b-8fa0-d54ded6b5214.assets.booqable.com/v2/booqable.js';
    script.async = true;
    document.body.appendChild(script);
    */

    // Initialize map
    if (map.current) return; // Initialize map only once
    if (!mapContainer.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng, lat],
      zoom: zoom,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add click handler to drop pin
    map.current.on('click', (e) => {
      const { lng, lat } = e.lngLat;

      // Update coordinates state
      setLng(parseFloat(lng.toFixed(6)));
      setLat(parseFloat(lat.toFixed(6)));

      // Remove existing marker if any
      if (marker.current) {
        marker.current.remove();
      }

      // Add new marker
      marker.current = new mapboxgl.Marker({ color: '#0078d4', draggable: true })
        .setLngLat([lng, lat])
        .addTo(map.current!);

      // Update coordinates when marker is dragged
      marker.current.on('dragend', () => {
        const lngLat = marker.current!.getLngLat();
        setLng(parseFloat(lngLat.lng.toFixed(6)));
        setLat(parseFloat(lngLat.lat.toFixed(6)));
      });
    });

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      /*if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }*/
    };
  }, []);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    if (!map.current) {
      alert('Map is not ready yet. Please wait a moment and try again.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { longitude, latitude } = position.coords;

        // Update state
        setLng(parseFloat(longitude.toFixed(6)));
        setLat(parseFloat(latitude.toFixed(6)));

        // Center map on current location
        if (map.current) {
          map.current.flyTo({ center: [longitude, latitude], zoom: 14, });

          // Remove existing marker if any
          if (marker.current) {
            marker.current.remove();
          }

          // Add marker at current location
          marker.current = new mapboxgl.Marker({ color: '#0078d4', draggable: true })
            .setLngLat([longitude, latitude])
            .addTo(map.current);

          // Update coordinates when marker is dragged
          marker.current.on('dragend', () => {
            const lngLat = marker.current!.getLngLat();
            setLng(parseFloat(lngLat.lng.toFixed(6)));
            setLat(parseFloat(lngLat.lat.toFixed(6)));
          });
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to retrieve your location');
      }
    );
  };

  const toggleMapStyle = () => {
    if (!map.current) return;

    const newStyle = mapStyle === 'streets' ? 'satellite' : 'streets';
    const styleUrl = newStyle === 'streets'
      ? 'mapbox://styles/mapbox/streets-v12'
      : 'mapbox://styles/mapbox/satellite-streets-v12';

    map.current.setStyle(styleUrl);
    setMapStyle(newStyle);
  };

  const handleTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as TabValue);
  };

  const handleEventClick = (event: any) => {
    console.log('Event clicked:', event);
  };

  const handleEventDoubleClick = (event: any) => {
    setSelectedOrderId(event.id);
    setOrderDialogOpen(true);
  };

  const handleDateClick = (date: Date) => {
    console.log('Date clicked:', date);
  };

  const handleCloseOrderDialog = () => {
    setOrderDialogOpen(false);
    setSelectedOrderId(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentCard}>
        <div className={styles.tabContainer}>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <button
                className={`${styles.calendarTabMenu} ${selectedTab === 'calendar' ? styles.calendarTabMenuActive : ''}`}
                onClick={() => setSelectedTab('calendar')}
              >
                <CalendarLtr20Regular className={styles.calendarIcon} />
                Calendar
                <ChevronDown20Regular />
              </button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setCalendarView('month')}>Month</MenuItem>
                <MenuItem onClick={() => setCalendarView('week')}>Week</MenuItem>
                <MenuItem onClick={() => setCalendarView('day')}>Day</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>

          <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
            <Tab value="booking">Booking</Tab>
          </TabList>
        </div>

        {selectedTab === 'calendar' && (
          <div className={styles.calendarContainer}>
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                Loading orders...
              </div>
            ) : (
              <BookingCalendar
                events={calendarEvents}
                onEventClick={handleEventClick}
                onEventDoubleClick={handleEventDoubleClick}
                onDateClick={handleDateClick}
              />
            )}
          </div>
        )}

        {selectedTab === 'booking' && (
          <div className={styles.bookingContent}>
            <h1 style={{ marginTop: 0 }}>Online Booking</h1>

            <div style={{ marginTop: '2rem' }}>
              <h3>Select Delivery Location</h3>
              <p style={{ color: tokens.colorNeutralForeground3, marginBottom: '16px' }}>
                Click on the map to drop a pin at your desired delivery location
              </p>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                <Button
                  appearance="primary"
                  onClick={handleUseCurrentLocation}
                >
                  Use Current Location
                </Button>

                <Button
                  appearance="secondary"
                  onClick={toggleMapStyle}
                >
                  {mapStyle === 'streets' ? 'Satellite View' : 'Street View'}
                </Button>
              </div>

              <div ref={mapContainer} className={styles.mapContainer} />

              <div className={styles.coordinatesContainer}>
                <div>
                  <Input value={lat.toString() + ", " + lng.toString()} readOnly />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Dialog */}
      <OrderDetailsDialog
        isOpen={orderDialogOpen}
        onClose={handleCloseOrderDialog}
        orderId={selectedOrderId}
      />
    </div>
  )
}